import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiRequestError } from "../../api/client";
import {
  CASE_STATUSES,
  CASE_STATUS_LABELS,
  DESIGN_VARIANTS,
  INVOICE_KIND_LABELS,
  professionLabel,
  siteUrl,
  type CaseStatus,
  type DesignVariant,
  type InvoiceKind,
} from "../../../shared/constants";
import {
  CaseStatusPill,
  InvoiceStatusPill,
  formatDate,
  formatDateTime,
  personName,
  yen,
} from "./ui";

interface AdminCaseDetail {
  id: string;
  user_id: string | null;
  guest_token: string | null;
  user_email: string | null;
  user_phone: string | null;
  user_last_name: string | null;
  user_first_name: string | null;
  slug: string | null;
  status: string;
  profession: string;
  office_name: string | null;
  office_address: string | null;
  office_phone: string | null;
  representative_name: string | null;
  practice_areas: string | null;
  published_url: string | null;
  created_at: string;
  updated_at: string;
}

interface UploadItem {
  id: string;
  kind: string;
  filename: string | null;
  content_type: string | null;
  size: number | null;
  url: string;
  created_at: string;
}

interface OfficeAssetItem {
  id: string;
  upload_id: string | null;
  asset_type: string;
  r2_key: string | null;
  filename: string | null;
  quality_score: number;
  permission_status: string;
  recommended_usage: unknown;
  notes: string | null;
  url: string | null;
  created_at: string;
}

interface TextAssetItem {
  id: string;
  kind: string;
  label: string;
  body: string;
  source: string;
}

interface ProposalItem {
  id: string;
  variant: string;
  image_url: string | null;
  selected: number;
  created_at: string;
}

interface ActivityItem {
  id: string;
  action: string;
  detail: string | null;
  created_at: string;
  actor_email: string | null;
  actor_last_name: string | null;
  actor_first_name: string | null;
}

interface NoteItem {
  id: string;
  body: string;
  created_at: string;
  author_email: string | null;
  author_last_name: string | null;
  author_first_name: string | null;
}

interface InvoiceItem {
  id: string;
  kind: string;
  amount_total: number;
  status: string;
  issued_at: string | null;
  created_at: string;
}

interface ChatHistoryItem {
  id: string;
  source: string;
  scope: string;
  page_id: string | null;
  role: "user" | "assistant" | "system";
  body: string;
  meta: Record<string, unknown> | null;
  created_at: string;
}

interface DetailResponse {
  case: AdminCaseDetail;
  is_guest: boolean;
  hearing: Record<string, unknown> | null;
  uploads: UploadItem[];
  office_assets: OfficeAssetItem[];
  text_assets: TextAssetItem[];
  proposals: ProposalItem[];
  chat_history: ChatHistoryItem[];
  activity: ActivityItem[];
  notes: NoteItem[];
  invoices: InvoiceItem[];
  balance: number;
}

const UPLOAD_KIND_LABELS: Record<string, string> = {
  face: "顔写真",
  office: "事務所写真",
  logo: "ロゴ",
  other: "その他",
  testimonial: "お客様の声",
  fee_table: "料金表",
  document: "資料",
};

const VARIANT_LABELS: Record<string, string> = Object.fromEntries(
  DESIGN_VARIANTS.map((v) => [v.value, v.label]),
) as Record<DesignVariant, string>;

export function AdminCaseDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusDraft, setStatusDraft] = useState<string>("");
  const [statusBusy, setStatusBusy] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [resetUrl, setResetUrl] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    api
      .get<DetailResponse>(`/api/admin/cases/${id}`)
      .then((res) => {
        setData(res);
        setStatusDraft(res.case.status);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const practiceAreas = useMemo(() => {
    if (!data?.case.practice_areas) return [];
    try {
      const arr = JSON.parse(data.case.practice_areas);
      return Array.isArray(arr) ? (arr as string[]) : [];
    } catch {
      return [];
    }
  }, [data]);

  const changeStatus = async () => {
    if (!id || !data || statusDraft === data.case.status) return;
    setStatusBusy(true);
    setActionError(null);
    try {
      await api.post(`/api/admin/cases/${id}/status`, { status: statusDraft });
      load();
    } catch (e) {
      setActionError(e instanceof ApiRequestError ? e.message : "ステータス変更に失敗しました");
    } finally {
      setStatusBusy(false);
    }
  };

  const addNote = async () => {
    if (!id) return;
    const body = noteDraft.trim();
    if (!body) return;
    setNoteBusy(true);
    setActionError(null);
    try {
      const note = await api.post<NoteItem>(`/api/admin/cases/${id}/notes`, {
        body,
      });
      setNoteDraft("");
      setData((prev) =>
        prev ? { ...prev, notes: [note, ...prev.notes] } : prev,
      );
    } catch (e) {
      setActionError(e instanceof ApiRequestError ? e.message : "メモの保存に失敗しました");
    } finally {
      setNoteBusy(false);
    }
  };

  const issueTempPassword = async () => {
    if (!data) return;
    setActionError(null);
    try {
      const res = await api.post<{ temp_password: string }>(
        `/api/admin/users/${data.case.user_id}/reset-password`,
      );
      setTempPassword(res.temp_password);
    } catch (e) {
      setActionError(e instanceof ApiRequestError ? e.message : "仮パスワード発行に失敗しました");
    }
  };

  const issueResetUrl = async () => {
    if (!data) return;
    setActionError(null);
    try {
      const res = await api.post<{ reset_url: string }>(
        `/api/admin/users/${data.case.user_id}/password-reset-link`,
      );
      setResetUrl(res.reset_url);
    } catch (e) {
      setActionError(e instanceof ApiRequestError ? e.message : "再設定URLの作成に失敗しました");
    }
  };

  if (loading) return <p className="muted">読み込み中...</p>;
  if (error || !data) {
    return (
      <section>
        <div className="alert alert-error">{error ?? "案件が見つかりません"}</div>
        <Link to="/admin">← 案件一覧へ戻る</Link>
      </section>
    );
  }

  const cs = data.case;
  const currentIndex = CASE_STATUSES.indexOf(cs.status as CaseStatus);

  return (
    <section className="admin-detail">
      <div className="breadcrumb">
        <Link to="/admin">案件一覧</Link> <span>/</span> 案件詳細
      </div>

      <div className="admin-page-head">
        <div>
          <h1>
            {cs.office_name ?? "（事務所名未設定）"}
            {data.is_guest && <span className="pill pill-purple">ゲスト営業</span>}
          </h1>
          <p className="muted">
            案件ID: {cs.id.slice(0, 8)} ・ 作成日: {formatDate(cs.created_at)} ・
            最終更新: {formatDateTime(cs.updated_at)}
          </p>
          <CaseStatusPill status={cs.status} />
        </div>
        <div className="head-actions">
          <select
            value={statusDraft}
            onChange={(e) => setStatusDraft(e.target.value)}
          >
            {CASE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {CASE_STATUS_LABELS[s as CaseStatus]}
              </option>
            ))}
          </select>
          <button
            className="btn btn-primary btn-sm"
            onClick={changeStatus}
            disabled={statusBusy || statusDraft === cs.status}
          >
            ステータス変更
          </button>
          {!data.is_guest && (
            <>
              <button className="btn btn-ghost btn-sm" onClick={issueResetUrl}>
                再設定URLを作成
              </button>
              <button className="btn btn-ghost btn-sm" onClick={issueTempPassword}>
                仮パスワード発行
              </button>
            </>
          )}
        </div>
      </div>

      {data.is_guest && cs.guest_token && (
        <div className="alert alert-info">
          ゲスト共有リンク（この見込み客にのみ共有）:{" "}
          <a href={`/g/${cs.guest_token}`} target="_blank" rel="noreferrer">
            {`${window.location.origin}/g/${cs.guest_token}`}
          </a>
        </div>
      )}

      {actionError && <div className="alert alert-error">{actionError}</div>}
      {tempPassword && (
        <div className="alert alert-info">
          仮パスワードを発行しました（顧客へ安全な方法でお伝えください。一度のみ表示）:
          <strong className="temp-pass"> {tempPassword}</strong>
        </div>
      )}
      {resetUrl && (
        <div className="alert alert-info">
          パスワード再設定URLを作成しました:
          <a href={resetUrl} target="_blank" rel="noreferrer"> {resetUrl}</a>
        </div>
      )}

      <div className="detail-grid">
        {/* 顧客・プロジェクト概要 */}
        <div className="card">
          <h2>顧客・プロジェクト概要</h2>
          <dl className="kv">
            <dt>事務所名</dt>
            <dd>{cs.office_name ?? "—"}</dd>
            <dt>代表者</dt>
            <dd>{cs.representative_name ?? "—"}</dd>
            <dt>士業種別</dt>
            <dd>{professionLabel(cs.profession)}</dd>
            <dt>メール</dt>
            <dd>{cs.user_email ?? "—（ゲスト未登録）"}</dd>
            <dt>電話</dt>
            <dd>{cs.office_phone ?? cs.user_phone ?? "—"}</dd>
            <dt>住所</dt>
            <dd>{cs.office_address ?? "—"}</dd>
            <dt>対応分野</dt>
            <dd>{practiceAreas.length ? practiceAreas.join(" / ") : "—"}</dd>
            <dt>公開URL</dt>
            <dd>
              {cs.published_url ? (
                <a href={cs.published_url} target="_blank" rel="noreferrer">
                  {cs.published_url}
                </a>
              ) : siteUrl(cs.slug) ? (
                <span className="muted">{siteUrl(cs.slug)}（公開準備中）</span>
              ) : (
                "—"
              )}
            </dd>
            <dt>クレジット残高</dt>
            <dd>{data.balance.toLocaleString("ja-JP")} cr</dd>
          </dl>
        </div>

        {/* プロジェクト進行状況 */}
        <div className="card">
          <h2>プロジェクト進行状況</h2>
          <ol className="steps">
            {CASE_STATUSES.map((s, i) => {
              const state =
                i < currentIndex ? "done" : i === currentIndex ? "current" : "todo";
              return (
                <li key={s} className={`step step-${state}`}>
                  <span className="step-marker">{i < currentIndex ? "✓" : i + 1}</span>
                  <span className="step-label">
                    {CASE_STATUS_LABELS[s as CaseStatus]}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      <div className="detail-grid">
        {/* ヒアリング内容 */}
        <div className="card">
          <h2>ヒアリング内容</h2>
          {data.hearing && Object.keys(data.hearing).length > 0 ? (
            <dl className="kv">
              {Object.entries(data.hearing).map(([k, v]) => (
                <div key={k} style={{ display: "contents" }}>
                  <dt>{k}</dt>
                  <dd>
                    {typeof v === "object"
                      ? JSON.stringify(v)
                      : String(v ?? "—")}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="muted">ヒアリング内容がありません。</p>
          )}
        </div>

        {/* アップロード素材 */}
        <div className="card">
          <h2>アップロード素材</h2>
          {data.uploads.length === 0 ? (
            <p className="muted">アップロードされた素材がありません。</p>
          ) : (
            <ul className="material-list">
              {data.uploads.map((u) => (
                <li key={u.id}>
                  <span className="pill pill-gray">
                    {UPLOAD_KIND_LABELS[u.kind] ?? u.kind}
                  </span>
                  <a href={u.url} target="_blank" rel="noreferrer">{u.filename ?? "（ファイル名なし）"}</a>
                  <span className="muted small">{u.content_type ?? "file"}{u.size ? ` / ${Math.round(u.size / 1024)}KB` : ""}</span>
                  <span className="muted small">{formatDate(u.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* テキスト素材 */}
        <div className="card">
          <h2>テキスト素材</h2>
          {data.text_assets.length === 0 ? (
            <p className="muted">テキストで入力された料金表・お客様の声はありません。</p>
          ) : (
            <ul className="material-list">
              {data.text_assets.map((asset) => (
                <li key={asset.id}>
                  <span className="pill pill-gray">
                    {UPLOAD_KIND_LABELS[asset.kind] ?? asset.kind}
                  </span>
                  <div>
                    <strong>{asset.label}</strong>
                    <p className="muted small" style={{ margin: "4px 0 0", whiteSpace: "pre-wrap" }}>
                      {asset.body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 素材登録記録 */}
        <div className="card">
          <h2>素材登録記録</h2>
          {data.office_assets.length === 0 ? (
            <p className="muted">制作素材として登録された記録がありません。</p>
          ) : (
            <ul className="material-list">
              {data.office_assets.map((asset) => (
                <li key={asset.id}>
                  <span className="pill pill-gray">
                    {UPLOAD_KIND_LABELS[asset.asset_type] ?? asset.asset_type}
                  </span>
                  {asset.url ? (
                    <a href={asset.url} target="_blank" rel="noreferrer">
                      {asset.filename ?? "素材ファイル"}
                    </a>
                  ) : (
                    <span>{asset.filename ?? "テキスト素材"}</span>
                  )}
                  <span className="muted small">{asset.permission_status}</span>
                  {asset.notes ? (
                    <span className="muted small" style={{ whiteSpace: "pre-wrap" }}>
                      {asset.notes}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* デザイン案 */}
      <div className="card">
        <h2>デザイン案</h2>
        {data.proposals.length === 0 ? (
          <p className="muted">デザイン案がまだ生成されていません。</p>
        ) : (
          <div className="thumb-grid">
            {data.proposals.map((p) => (
              <div
                key={p.id}
                className={`thumb ${p.selected ? "thumb-selected" : ""}`}
              >
                {p.image_url ? (
                  <img src={p.image_url} alt={VARIANT_LABELS[p.variant] ?? p.variant} />
                ) : (
                  <div className="thumb-empty">画像なし</div>
                )}
                <div className="thumb-caption">
                  {VARIANT_LABELS[p.variant] ?? p.variant}
                  {p.selected ? <span className="pill pill-green">選択中</span> : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="detail-grid">
        {/* チャット履歴 */}
        <div className="card">
          <h2>チャット履歴</h2>
          {data.chat_history.length === 0 ? (
            <p className="muted">チャット履歴がありません。</p>
          ) : (
            <ul className="activity-list">
              {data.chat_history.map((m) => (
                <li key={m.id}>
                  <div className="activity-time muted small">
                    {formatDateTime(m.created_at)}
                  </div>
                  <div className="activity-body">
                    <span className="activity-actor">
                      {m.scope} / {m.role === "user" ? "ユーザー" : "AI"}
                    </span>
                    <span>{m.body}</span>
                    {m.page_id ? <span className="muted small">page: {m.page_id}</span> : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 活動履歴 */}
        <div className="card">
          <h2>活動履歴</h2>
          {data.activity.length === 0 ? (
            <p className="muted">活動履歴がありません。</p>
          ) : (
            <ul className="activity-list">
              {data.activity.map((a) => (
                <li key={a.id}>
                  <div className="activity-time muted small">
                    {formatDateTime(a.created_at)}
                  </div>
                  <div className="activity-body">
                    <span className="activity-actor">
                      {personName(a.actor_last_name, a.actor_first_name, "システム")}
                    </span>
                    <span>{a.detail ?? a.action}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 社内メモ */}
        <div className="card">
          <h2>社内メモ</h2>
          <div className="note-form">
            <textarea
              placeholder="メモを入力してください..."
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              rows={3}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={addNote}
              disabled={noteBusy || !noteDraft.trim()}
            >
              メモを保存
            </button>
          </div>
          {data.notes.length === 0 ? (
            <p className="muted">社内メモはまだありません。</p>
          ) : (
            <ul className="note-list">
              {data.notes.map((n) => (
                <li key={n.id}>
                  <div className="note-meta muted small">
                    {personName(n.author_last_name, n.author_first_name, n.author_email ?? "運営")}
                    ・{formatDateTime(n.created_at)}
                  </div>
                  <div className="note-body">{n.body}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* 請求・クレジット */}
      <div className="card">
        <h2>この案件の請求</h2>
        {data.invoices.length === 0 ? (
          <p className="muted">請求はありません。</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>種別</th>
                <th>金額（税込）</th>
                <th>ステータス</th>
                <th>請求日</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((iv) => (
                <tr key={iv.id}>
                  <td>{INVOICE_KIND_LABELS[iv.kind as InvoiceKind] ?? iv.kind}</td>
                  <td>{yen(iv.amount_total)}</td>
                  <td>
                    <InvoiceStatusPill status={iv.status} />
                  </td>
                  <td>{formatDate(iv.issued_at ?? iv.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
